import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@babackapp/ui/components/card";
import { ClipboardCheck, ClipboardCopy } from "lucide-react";
import { useState } from "react";

const TOKEN_PLACEHOLDER = "<your-token>";

function mcpUrl() {
  const base: string = import.meta.env.VITE_API_URL ?? "http://localhost:3040";
  return `${base.replace(/\/$/, "")}/mcp`;
}

function claudeCodeCommand(token: string) {
  return `claude mcp add --transport http baback ${mcpUrl()} \\
  --header "Authorization: Bearer ${token}"`;
}

function claudeConfig(token: string) {
  return JSON.stringify(
    {
      mcpServers: {
        baback: {
          type: "http",
          url: mcpUrl(),
          headers: { Authorization: `Bearer ${token}` },
        },
      },
    },
    null,
    2,
  );
}

function curlCommand(token: string) {
  return `curl -s ${mcpUrl()} \\
  -H "Authorization: Bearer ${token}" \\
  -H "Content-Type: application/json" \\
  -H "Accept: application/json, text/event-stream" \\
  -d '{"jsonrpc":"2.0","id":1,"method":"tools/list"}'`;
}

interface McpSnippetsCardProps {
  /** The freshly created token, when there is one: snippets are then ready to paste. */
  token?: string;
}

export default function McpSnippetsCard({ token }: McpSnippetsCardProps) {
  const value = token ?? TOKEN_PLACEHOLDER;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Connect an MCP client</CardTitle>
        <CardDescription>
          {token
            ? "Snippets below already carry the token you just created."
            : `Create a token above, or replace ${TOKEN_PLACEHOLDER} with an existing one.`}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-6">
        <Snippet
          title="Claude Code"
          language="bash"
          code={claudeCodeCommand(value)}
        />
        <Snippet
          title="Claude Desktop / mcp.json"
          language="json"
          code={claudeConfig(value)}
        />
        <Snippet
          title="Quick check, without any client"
          language="bash"
          code={curlCommand(value)}
        />
      </CardContent>
    </Card>
  );
}

interface SnippetProps {
  title: string;
  language: string;
  code: string;
}

function Snippet({ title, language, code }: SnippetProps) {
  const [hasCopied, setHasCopied] = useState(false);

  const handleCopy = () => {
    void navigator.clipboard.writeText(code);
    setHasCopied(true);
    setTimeout(() => setHasCopied(false), 2000);
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">{title}</span>
        <button
          type="button"
          onClick={handleCopy}
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          {hasCopied ? (
            <>
              <ClipboardCheck className="size-3.5 text-green-500" />
              Copied
            </>
          ) : (
            <>
              <ClipboardCopy className="size-3.5" />
              Copy
            </>
          )}
        </button>
      </div>
      <pre className="overflow-x-auto rounded border bg-muted px-3 py-2 text-xs">
        <code data-language={language}>{code}</code>
      </pre>
    </div>
  );
}
